// src/utils/analyticsTracker.js
// Lightweight analytics event tracking to Firestore
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Persist session ID in localStorage so it survives page reloads, in-app browser
// re-initializations (Facebook/Instagram WebViews), and bfcache evictions.
// sessionStorage is unreliable in WebViews and can be wiped on scroll-triggered
// reloads. localStorage + a 30-min inactivity timeout matches GA4 session semantics.
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
let _cachedSessionId = null; // in-memory fallback if all storage fails
const getOrCreateSessionId = () => {
  const KEY = 'rb_session_id';
  const TS_KEY = 'rb_session_ts';
  const now = Date.now();

  // Try localStorage first (most reliable), fall back to sessionStorage
  const storage = (() => {
    try { localStorage.setItem('_rb_test', '1'); localStorage.removeItem('_rb_test'); return localStorage; } catch {}
    try { sessionStorage.setItem('_rb_test', '1'); sessionStorage.removeItem('_rb_test'); return sessionStorage; } catch {}
    return null;
  })();

  if (storage) {
    const lastActivity = parseInt(storage.getItem(TS_KEY) || '0', 10);
    let id = storage.getItem(KEY);
    if (!id || (now - lastActivity > SESSION_TIMEOUT_MS)) {
      id = `${now}_${Math.random().toString(36).substr(2, 9)}`;
      storage.setItem(KEY, id);
    }
    storage.setItem(TS_KEY, String(now));
    _cachedSessionId = id;
    return id;
  }

  // No storage available — reuse cached ID if we have one (same page lifecycle)
  if (!_cachedSessionId) {
    _cachedSessionId = `${now}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return _cachedSessionId;
};
const SESSION_ID = getOrCreateSessionId();

// Suppress all tracking when running inside an iframe. Firebase Auth loads a
// hidden iframe at /__/auth/iframe for session management; because firebase.json
// rewrites ** → /index.html, that iframe boots a full copy of the React SPA,
// which then hits the catch-all route and fires a phantom page_view('home')
// with the same localStorage sessionId as the parent window. Guarding here
// short-circuits ALL analytics writes from iframe contexts.
const _isInIframe = (() => {
  try { return typeof window !== 'undefined' && window.self !== window.top; } catch { return true; }
})();

// Suppress all tracking for admin/owner users.
// Uses localStorage so it persists across sessions — once you log in as admin
// on a device, anonymous browsing on that device is also excluded.
const INTERNAL_KEY = 'rb_internal';
let _isInternalUser = false;
try { _isInternalUser = localStorage.getItem(INTERNAL_KEY) === '1'; } catch {}

export const setInternalUser = (isInternal) => {
  _isInternalUser = isInternal;
  try {
    if (isInternal) {
      localStorage.setItem(INTERNAL_KEY, '1');
    } else {
      localStorage.removeItem(INTERNAL_KEY);
    }
  } catch {}
};

// Capture referral info once on page load
const _detectReferral = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const rawReferrer = document.referrer || '';

  let source = 'direct';
  if (utmSource) {
    source = utmSource.toLowerCase();
  } else if (rawReferrer) {
    try {
      const refHost = new URL(rawReferrer).hostname.toLowerCase();
      if (refHost.includes('instagram')) source = 'instagram';
      else if (refHost.includes('facebook') || refHost.includes('fb.com')) source = 'facebook';
      else if (refHost.includes('pinterest')) source = 'pinterest';
      else if (refHost.includes('twitter') || refHost.includes('x.com')) source = 'twitter';
      else if (refHost.includes('tiktok')) source = 'tiktok';
      else if (refHost.includes('youtube')) source = 'youtube';
      else if (refHost.includes('google')) source = 'google';
      else if (refHost.includes('bing')) source = 'bing';
      else if (refHost.includes('localhost') || refHost.includes('127.0.0.1')) source = 'direct';
      else if (refHost.includes('brightreading.app') || refHost.includes('learning-app-6f8ff')) source = 'direct';
      else source = refHost;
    } catch {
      source = 'unknown';
    }
  }

  return { source, medium: utmMedium || '', campaign: utmCampaign || '', rawReferrer };
};

// Persist the FIRST external referral for the session so mid-session reloads
// (common in FB/IG in-app browsers) don't overwrite "facebook" with "direct".
const _refStorage = (() => {
  try { localStorage.setItem('_rb_test', '1'); localStorage.removeItem('_rb_test'); return localStorage; } catch {}
  try { sessionStorage.setItem('_rb_test', '1'); sessionStorage.removeItem('_rb_test'); return sessionStorage; } catch {}
  return null;
})();
const REF_KEY = 'rb_session_ref';
const REF_SID_KEY = 'rb_ref_sid';

const getReferralSource = () => {
  const fresh = _detectReferral();

  if (_refStorage) {
    const savedSid = _refStorage.getItem(REF_SID_KEY);
    // Same session — prefer the saved referral unless fresh has a real UTM/external source
    if (savedSid === SESSION_ID) {
      const saved = _refStorage.getItem(REF_KEY);
      if (saved) {
        try {
          const prev = JSON.parse(saved);
          // Only overwrite if fresh has a stronger signal (UTM or non-direct external)
          if (fresh.source !== 'direct' && fresh.source !== prev.source) {
            _refStorage.setItem(REF_KEY, JSON.stringify(fresh));
            return fresh;
          }
          return prev;
        } catch {}
      }
    } else {
      // New session — reset
      _refStorage.setItem(REF_SID_KEY, SESSION_ID);
    }
    // New session or no saved referral — store this one
    _refStorage.setItem(REF_KEY, JSON.stringify(fresh));
  }
  return fresh;
};

const referral = getReferralSource();

// Multi-signal device detection — combines 6 signals for accuracy.
// UA strings alone are unreliable: iPadOS sends a macOS UA, Chrome has frozen
// UA details since v107, and many mobile browsers spoof desktop strings.
//
// Signals used (in priority order):
//   1. User-Agent Client Hints API (navigator.userAgentData.mobile) — Chromium only
//   2. CSS pointer media query — coarse = finger/touch, fine = mouse
//   3. CSS hover media query — hover:none = touch-only device
//   4. navigator.maxTouchPoints — iPad returns 5+, Mac returns 0-1
//   5. Screen dimensions — short side < 600px = phone, 600-1024 = tablet
//   6. UA string regex — least reliable, used as final tiebreaker
//
// Sources:
//   https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData
//   https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer
//   https://developer.mozilla.org/en-US/docs/Web/CSS/@media/hover
//   https://web.dev/learn/design/interaction
const getDeviceInfo = () => {
  const ua = navigator.userAgent || '';
  const shortSide = Math.min(screen.width, screen.height);

  // Signal 1: Client Hints (Chromium 89+ — Chrome, Edge, Opera ≈ 65% of traffic)
  if (navigator.userAgentData) {
    if (navigator.userAgentData.mobile) {
      // Client Hints says mobile — distinguish phone vs tablet by screen size
      return { deviceType: shortSide >= 600 ? 'tablet' : 'mobile' };
    }
    // Client Hints says not-mobile, but check for iPad masquerading as Mac
    if (navigator.maxTouchPoints > 2) {
      return { deviceType: 'tablet' };
    }
    return { deviceType: 'desktop' };
  }

  // Signal 2-3: Pointer and hover media queries (all modern browsers incl. Safari/Firefox)
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;

  // Signal 4: Touch capability
  const hasTouchScreen = navigator.maxTouchPoints > 0;

  // Signal 5: Screen size thresholds
  const isSmallScreen = shortSide < 600;
  const isMediumScreen = shortSide >= 600 && shortSide < 1024;

  // Signal 6: UA string patterns (least reliable — used as tiebreaker only)
  const uaIsTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  const uaIsMobile = /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua);

  // iPad-as-Mac detection: iPadOS 13+ sends Macintosh UA but has 5+ touch points
  const isIPadAsMac = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 2;

  // Classification — most specific signals first
  if (isIPadAsMac || uaIsTablet || (hasCoarsePointer && isMediumScreen)) {
    return { deviceType: 'tablet' };
  }
  if (uaIsMobile || (hasCoarsePointer && isSmallScreen && !canHover)) {
    return { deviceType: 'mobile' };
  }
  if (hasTouchScreen && !canHover && hasCoarsePointer) {
    return { deviceType: 'mobile' };
  }
  return { deviceType: 'desktop' };
};

const device = getDeviceInfo();

// Deduplicate events within the same session. Uses localStorage (with the
// session ID as a scoping key) so dedup survives page reloads, StrictMode
// double-mounts, bfcache restores, and in-app browser re-initializations.
// When a new session starts (new SESSION_ID), old dedup data is cleared.
const FIRED_KEY = 'rb_fired';
const FIRED_SID_KEY = 'rb_fired_sid';
const _firedEvents = new Set();

// Restore already-fired keys — only if they belong to the current session.
// Try both storage backends for resilience.
const _dedupStorage = (() => {
  try { localStorage.setItem('_rb_test', '1'); localStorage.removeItem('_rb_test'); return localStorage; } catch {}
  try { sessionStorage.setItem('_rb_test', '1'); sessionStorage.removeItem('_rb_test'); return sessionStorage; } catch {}
  return null;
})();

try {
  if (_dedupStorage) {
    const savedSid = _dedupStorage.getItem(FIRED_SID_KEY);
    if (savedSid === SESSION_ID) {
      const saved = _dedupStorage.getItem(FIRED_KEY);
      if (saved) JSON.parse(saved).forEach(k => _firedEvents.add(k));
    } else {
      // New session — clear stale dedup data
      _dedupStorage.setItem(FIRED_SID_KEY, SESSION_ID);
      _dedupStorage.removeItem(FIRED_KEY);
    }
  }
} catch {}

const _markFired = (key) => {
  _firedEvents.add(key);
  try {
    if (_dedupStorage) {
      _dedupStorage.setItem(FIRED_KEY, JSON.stringify([..._firedEvents]));
    }
  } catch {}
};

// Batched event writing — queue events and flush every 5 seconds to avoid
// flooding mobile Safari with floating Promises and IndexedDB writes.
// Flush immediately on page hide/visibilitychange so events aren't lost.
let _eventCounter = 0;
const FLUSH_INTERVAL_MS = 5000;
const _eventQueue = [];
let _flushTimer = null;

const _flush = async () => {
  if (_eventQueue.length === 0) return;
  const batch = _eventQueue.splice(0, _eventQueue.length);
  const eventsCol = collection(db, 'analytics_events');
  for (const event of batch) {
    try {
      await addDoc(eventsCol, event);
    } catch (err) {
      console.debug('Analytics write failed:', err.message);
    }
  }
};

const _scheduleFlush = () => {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush();
  }, FLUSH_INTERVAL_MS);
};

// Flush on page hide so events aren't lost during navigation
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') _flush();
  });
  window.addEventListener('pagehide', () => _flush());
}

const writeEvent = (event) => {
  if (_isInternalUser || _isInIframe) return;
  try {
    if (_dedupStorage) _dedupStorage.setItem('rb_session_ts', String(Date.now()));
  } catch {}
  const eventId = `${SESSION_ID}_${++_eventCounter}_${Date.now()}`;
  _eventQueue.push({
    ...event,
    eventId,
    timestamp: serverTimestamp(),
    sessionId: SESSION_ID,
    deviceType: device.deviceType,
    screenWidth: screen.width,
    screenHeight: screen.height,
    referralSource: referral.source,
    referralMedium: referral.medium,
    referralCampaign: referral.campaign,
    referrer: referral.rawReferrer,
  });
  _scheduleFlush();
};

// trackPageView fires ONCE per page name per session (deduped). Optional
// `metadata` is attached to the event so the admin dashboard can show
// navigation context (where the user came from, in-app vs browser back,
// initial cold land vs SPA navigation, etc.). Page view callers SHOULD
// pass nav metadata via the `usePageView` hook (src/utils/usePageView.js)
// — that hook auto-captures React Router's location.key, navigationType,
// and location.state.navSource. Imperative callers can pass metadata too.
export const trackPageView = (pageName, userId = null, metadata = null) => {
  const key = `pv_${pageName}`;
  if (_firedEvents.has(key)) return;
  _markFired(key);

  writeEvent({
    type: 'page_view',
    page: pageName,
    userId,
    // Only attach metadata field if non-empty — keeps Firestore docs lean.
    ...(metadata && Object.keys(metadata).length > 0 && { metadata }),
  });
};

export const trackEvent = (eventType, metadata = {}, userId = null) => {
  // Deduplicate section_view and scroll_depth — fire once per visit.
  // Allow repeated cta_click, signup, lesson_start, lesson_complete (intentional user actions).
  if (eventType === 'section_view' || eventType === 'scroll_depth') {
    const key = `${eventType}_${metadata.section || metadata.depth || ''}`;
    if (_firedEvents.has(key)) return;
    _markFired(key);
  }
  writeEvent({
    type: eventType,
    userId,
    metadata,
  });
};
